import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { BrandWithProfiles, InsertBrand, InsertBrandAudienceProfile, InsertCampaign, INDUSTRIES } from "@shared/schema";
import { MultiSelect } from "@/components/ui/multi-select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { User, Mail, Building, Calendar, Camera, Upload, Bell, Phone, Briefcase, X, Info, Crop as CropIcon, Check, CheckCircle2, HelpCircle, Lightbulb, BarChart3, TrendingUp, Users, Target, Award, Star, Trophy, Zap, Plus, Edit2, Trash2, MapPin, DollarSign, Settings, Eye, EyeOff, PieChart, ChevronsUpDown, Loader2 } from "lucide-react";
import { MaxSpinner } from "@/components/ui/max-spinner";
import Navbar from "@/components/navbar";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import QuickPersonaCreator from "@/components/quick-persona-creator";
import ProfileHeader from "./ProfileHeader";
import ImageCropModal from "./ImageCropModal";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { CampaignFormData, DeleteConfirmData } from "./types";
import { generateBrandPortfolioInsights, calculateProfileCompletion, getCroppedImg as getCroppedImgUtil } from "./profileUtils";

// Removed unused useFilterOptions hook - now handled by QuickPersonaCreator

const profileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  company: z.string().optional(),
  jobTitle: z.string().optional(),
  phone: z.string().optional(),
  companyType: z.enum(["brand", "agency", ""]).optional(),
  industry: z.array(z.string()).optional(),

  companySize: z.string().optional(),
  websiteUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),

  profileImageUrl: z.string().optional(),
  companyLogoUrl: z.string().optional(),
  emailShowUpdates: z.boolean().optional(),
  emailArtistUpdates: z.boolean().optional(),
  emailMaxUpdates: z.boolean().optional(),
  currentPassword: z.string().optional(),
  newPassword: z.string().optional(),
  confirmPassword: z.string().optional(),
}).refine((data) => {
  if (data.newPassword && !data.currentPassword) {
    return false;
  }
  if (data.newPassword && data.newPassword !== data.confirmPassword) {
    return false;
  }
  return true;
}, {
  message: "Password confirmation doesn't match",
  path: ["confirmPassword"],
});

export default function Profile() {
  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY EARLY RETURNS
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showCropModal, setShowCropModal] = useState(false);
  const [showBrandWidget, setShowBrandWidget] = useState(false);

  // Brand management state
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<BrandWithProfiles | null>(null);
  const [brandFormData, setBrandFormData] = useState<Partial<InsertBrand>>({});
  const [brandImageFile, setBrandImageFile] = useState<File | null>(null);
  const [brandImagePreview, setBrandImagePreview] = useState<string>('');
  const [brandCrop, setBrandCrop] = useState<Crop>();
  const [brandCompletedCrop, setBrandCompletedCrop] = useState<PixelCrop>();
  const [showBrandImageCrop, setShowBrandImageCrop] = useState<boolean>(false);
  
  // Audience profile management state
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [selectedBrandForAudience, setSelectedBrandForAudience] = useState<BrandWithProfiles | null>(null);
  const [editingAudienceProfile, setEditingAudienceProfile] = useState<any>(null);
  
  // Campaign management state
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedBrandForCampaign, setSelectedBrandForCampaign] = useState<BrandWithProfiles | null>(null);
  
  // Confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState<{
    type: 'brand' | 'persona' | 'campaign';
    name: string;
    id: number;
  } | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<any>(null);
  const [campaignFormData, setCampaignFormData] = useState<CampaignFormData>({});
  
  // Dynamic Create Button States
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showBrandSelectionModal, setShowBrandSelectionModal] = useState(false);
  const [brandSelectionType, setBrandSelectionType] = useState<'audience' | 'campaign' | null>(null);
  
  // Fetch brands data
  const { data: brands = [], isLoading: brandsLoading } = useQuery<BrandWithProfiles[]>({
    queryKey: ['/api/brands'],
    enabled: !!user,
  });

  // Brand mutations
  const createBrandMutation = useMutation({
    mutationFn: async (brandData: InsertBrand) => {
      return await apiRequest('/api/brands', {
        method: 'POST',
        body: JSON.stringify(brandData),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({
        title: "Brand Created",
        description: "Your brand profile has been successfully created.",
      });
      setShowBrandModal(false);
      setBrandFormData({});
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create brand. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateBrandMutation = useMutation({
    mutationFn: async ({ id, brandData }: { id: number; brandData: Partial<InsertBrand> }) => {
      return await apiRequest(`/api/brands/${id}`, {
        method: 'PUT',
        body: JSON.stringify(brandData),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({
        title: "Brand Updated",
        description: "Your brand profile has been successfully updated.",
      });
      setEditingBrand(null);
      setShowBrandModal(false);
      setBrandFormData({});
    },
  });

  const deleteBrandMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/brands/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({
        title: "Brand Deleted",
        description: "Your brand profile has been successfully deleted.",
      });
    },
  });

  // Audience Profile mutations
  const createAudienceProfileMutation = useMutation({
    mutationFn: async (profileData: InsertBrandAudienceProfile) => {
      return await apiRequest('/api/audience-profiles', {
        method: 'POST',
        body: JSON.stringify(profileData),
      });
    },
    onSuccess: async () => {
      // Force refetch brands data and wait for completion
      await queryClient.refetchQueries({ queryKey: ['/api/brands'] });
      
      // Update selectedBrandForAudience with fresh data if it's currently selected
      if (selectedBrandForAudience) {
        const updatedBrands = queryClient.getQueryData(['/api/brands']) as BrandWithProfiles[];
        const updatedBrand = updatedBrands?.find(brand => brand.id === selectedBrandForAudience.id);
        if (updatedBrand) {
          setSelectedBrandForAudience(updatedBrand);
        }
      }
      
      toast({
        title: "Persona Created",
        description: "Your persona has been successfully created.",
      });
      setShowAudienceModal(false);
    },
  });

  const updateAudienceProfileMutation = useMutation({
    mutationFn: async ({ id, profileData }: { id: number; profileData: Partial<InsertBrandAudienceProfile> }) => {
      return await apiRequest(`/api/audience-profiles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
    },
    onSuccess: async () => {
      // Force refetch brands data and wait for completion
      await queryClient.refetchQueries({ queryKey: ['/api/brands'] });
      
      // Update selectedBrandForAudience with fresh data if it's currently selected
      if (selectedBrandForAudience) {
        const updatedBrands = queryClient.getQueryData(['/api/brands']) as BrandWithProfiles[];
        const updatedBrand = updatedBrands?.find(brand => brand.id === selectedBrandForAudience.id);
        if (updatedBrand) {
          setSelectedBrandForAudience(updatedBrand);
        }
      }
      
      toast({
        title: "Persona Updated",
        description: "Your persona has been successfully updated.",
      });
      setShowAudienceModal(false);
      setEditingAudienceProfile(null);
    },
  });

  const deleteAudienceProfileMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/audience-profiles/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async (_, deletedId) => {
      // Force refetch brands data and wait for completion
      await queryClient.refetchQueries({ queryKey: ['/api/brands'] });
      
      // Update selectedBrandForAudience with fresh data if it's currently selected
      if (selectedBrandForAudience) {
        const updatedBrands = queryClient.getQueryData(['/api/brands']) as BrandWithProfiles[];
        const updatedBrand = updatedBrands?.find(brand => brand.id === selectedBrandForAudience.id);
        if (updatedBrand) {
          setSelectedBrandForAudience(updatedBrand);
        }
      }
      
      // Update editingBrand with fresh data if it's currently being edited
      if (editingBrand) {
        const updatedBrands = queryClient.getQueryData(['/api/brands']) as BrandWithProfiles[];
        const updatedBrand = updatedBrands?.find(brand => brand.id === editingBrand.id);
        if (updatedBrand) {
          setEditingBrand(updatedBrand);
        }
      }
      
      // Clear any editing state if we just deleted the profile being edited
      if (editingAudienceProfile && editingAudienceProfile.id === deletedId) {
        setEditingAudienceProfile(null);
      }
      
      toast({
        title: "Persona Deleted",
        description: "Your persona has been successfully deleted.",
      });
    },
    onError: (error: any) => {
      // Handle deletion error silently in production
      toast({
        title: "Error Deleting Profile",
        description: error.message || "Failed to delete persona. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Campaign mutations
  const createCampaignMutation = useMutation({
    mutationFn: async (campaignData: InsertCampaign) => {
      return await apiRequest('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify(campaignData),
      });
    },
    onSuccess: () => {
      // Force refetch brands data
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      queryClient.refetchQueries({ queryKey: ['/api/brands'] });
      toast({
        title: "Campaign Created",
        description: "Your campaign has been successfully created.",
      });
      setShowCampaignModal(false);
      setCampaignFormData({});
    },
  });

  const updateCampaignMutation = useMutation({
    mutationFn: async ({ id, campaignData }: { id: number; campaignData: Partial<InsertCampaign> }) => {
      return await apiRequest(`/api/campaigns/${id}`, {
        method: 'PUT',
        body: JSON.stringify(campaignData),
      });
    },
    onSuccess: () => {
      // Force refetch brands data
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      queryClient.refetchQueries({ queryKey: ['/api/brands'] });
      toast({
        title: "Campaign Updated",
        description: "Your campaign has been successfully updated.",
      });
      setShowCampaignModal(false);
      setEditingCampaign(null);
      setCampaignFormData({});
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/campaigns/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async () => {
      // Force refetch brands data and wait for completion
      await queryClient.refetchQueries({ queryKey: ['/api/brands'] });
      
      // Update selectedBrandForAudience with fresh data if it's currently selected
      if (selectedBrandForAudience) {
        const updatedBrands = queryClient.getQueryData(['/api/brands']) as BrandWithProfiles[];
        const updatedBrand = updatedBrands?.find(brand => brand.id === selectedBrandForAudience.id);
        if (updatedBrand) {
          setSelectedBrandForAudience(updatedBrand);
        }
      }
      
      // Update editingBrand with fresh data if it's currently being edited
      if (editingBrand) {
        const updatedBrands = queryClient.getQueryData(['/api/brands']) as BrandWithProfiles[];
        const updatedBrand = updatedBrands?.find(brand => brand.id === editingBrand.id);
        if (updatedBrand) {
          setEditingBrand(updatedBrand);
        }
      }
      
      toast({
        title: "Campaign Deleted",
        description: "Your campaign has been successfully deleted.",
      });
    },
  });

  // Helper functions for delete confirmations
  const handleDeleteConfirm = (type: 'brand' | 'persona' | 'campaign', name: string, id: number) => {
    setDeleteConfirmData({ type, name, id });
    setShowDeleteConfirm(true);
  };

  const executeDelete = () => {
    if (!deleteConfirmData) return;
    
    switch (deleteConfirmData.type) {
      case 'brand':
        deleteBrandMutation.mutate(deleteConfirmData.id);
        break;
      case 'persona':
        deleteAudienceProfileMutation.mutate(deleteConfirmData.id);
        break;
      case 'campaign':
        deleteCampaignMutation.mutate(deleteConfirmData.id);
        break;
    }
    
    setShowDeleteConfirm(false);
    setDeleteConfirmData(null);
  };

  const [imageSrc, setImageSrc] = useState<string>("");
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [croppingFor, setCroppingFor] = useState<'profile' | 'logo'>('profile');
  const imageRef = useRef<HTMLImageElement>(null);
  const brandImageRef = useRef<HTMLImageElement>(null);
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      company: user?.company || "",
      jobTitle: user?.jobTitle || "",
      phone: user?.phone || "",
      companyType: (user?.companyType as "" | "brand" | "agency") || "",
      industry: (user as any)?.industry || [],

      companySize: (user as any)?.companySize || "",
      websiteUrl: (user as any)?.websiteUrl || "",

      profileImageUrl: user?.profileImageUrl || "",
      companyLogoUrl: user?.companyLogoUrl || "",
      emailShowUpdates: user?.emailShowUpdates || false,
      emailArtistUpdates: user?.emailArtistUpdates || false,
      emailMaxUpdates: user?.emailMaxUpdates || false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });



  // Update form when user data changes
  useEffect(() => {
    if (user && !form.formState.isDirty) {
      // Only reset form if it's not dirty (hasn't been modified by user)
      form.reset({
        name: user.name || "",
        email: user.email || "",
        company: user.company || "",
        jobTitle: user.jobTitle || "",
        phone: user.phone || "",
        companyType: (user.companyType as "" | "brand" | "agency") || "",
        industry: (user as any).industry || [],

        companySize: (user as any).companySize || "",
        websiteUrl: (user as any).websiteUrl || "",
        profileImageUrl: user.profileImageUrl || "",
        companyLogoUrl: user.companyLogoUrl || "",
        emailShowUpdates: user.emailShowUpdates || false,
        emailArtistUpdates: user.emailArtistUpdates || false,
        emailMaxUpdates: user.emailMaxUpdates || false,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [user, form]);



  // Brand Portfolio Insights based on actual brand data
  const generateBrandPortfolioInsights = useCallback(() => {
    if (!brands.length) {
      return {
        totalBrands: 0,
        totalAudienceProfiles: 0,
        totalCampaigns: 0,
        activeCampaigns: 0,
        industryBreakdown: {},
        audienceReach: 0,
        campaignBudgets: { total: 0, average: 0 },
        completionRate: 0,
        recommendations: ["Create your first brand to get started with portfolio insights"]
      };
    }

    // Count audience profiles and campaigns across all brands
    const totalAudienceProfiles = brands.reduce((sum, brand) => sum + (brand.audienceProfiles?.length || 0), 0);
    const totalCampaigns = brands.reduce((sum, brand) => sum + (brand.campaigns?.length || 0), 0);
    
    // Count active campaigns (assuming "active" and "planning" statuses are active)
    const activeCampaigns = brands.reduce((sum, brand) => 
      sum + (brand.campaigns?.filter(c => c.status && ['active', 'planning'].includes(c.status)).length || 0), 0);

    // Industry breakdown from actual brand data
    const industryBreakdown = brands.reduce((acc, brand) => {
      const industry = Array.isArray(brand.industry) ? brand.industry[0] : (brand.industry || 'Other');
      acc[industry] = (acc[industry] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate total audience reach from audience profiles
    const audienceReach = brands.reduce((sum, brand) => {
      return sum + (brand.audienceProfiles?.reduce((profileSum, profile) => {
        // Estimate reach based on age ranges and demographics
        const ageRangeCount = profile.ageRanges?.length || 1;
        const genderCount = profile.genders?.length || 1;
        const locationCount = (profile as any).geographicFocus?.split(',').length || 1;
        return profileSum + (ageRangeCount * genderCount * locationCount * 50000); // Rough estimate
      }, 0) || 0);
    }, 0);

    // Campaign budget analysis
    const campaignBudgets = brands.reduce((acc, brand) => {
      brand.campaigns?.forEach(campaign => {
        if (campaign.budgetRange) {
          // Parse budget range like "$10,000-$25,000"
          const budgetMatch = campaign.budgetRange.match(/\$?([\d,]+)/);
          if (budgetMatch) {
            const budgetValue = parseInt(budgetMatch[1].replace(/,/g, ''));
            acc.total += budgetValue;
            acc.count += 1;
          }
        }
      });
      return acc;
    }, { total: 0, count: 0 });

    const averageBudget = campaignBudgets.count > 0 ? Math.round(campaignBudgets.total / campaignBudgets.count) : 0;

    // Portfolio completion rate
    const maxPossibleScore = brands.length * 4; // 4 points per brand (name, industry, audience, campaign)
    const actualScore = brands.reduce((sum, brand) => {
      let brandScore = 0;
      if (brand.name) brandScore += 1;
      if (brand.industry) brandScore += 1;
      if (brand.audienceProfiles?.length) brandScore += 1;
      if (brand.campaigns?.length) brandScore += 1;
      return sum + brandScore;
    }, 0);
    
    const completionRate = maxPossibleScore > 0 ? Math.round((actualScore / maxPossibleScore) * 100) : 0;

    // Generate recommendations based on data
    const recommendations = [];
    if (totalAudienceProfiles === 0) {
      recommendations.push("Add personas to better target your campaigns");
    }
    if (totalCampaigns === 0) {
      recommendations.push("Create campaigns to start tracking your sponsorship activities");
    }
    if (Object.keys(industryBreakdown).length === 1) {
      recommendations.push("Consider diversifying across multiple industries for broader reach");
    }
    if (activeCampaigns < totalCampaigns / 2) {
      recommendations.push("Review inactive campaigns - consider reactivating or archiving them");
    }

    return {
      totalBrands: brands.length,
      totalAudienceProfiles,
      totalCampaigns,
      activeCampaigns,
      industryBreakdown,
      audienceReach,
      campaignBudgets: { 
        total: campaignBudgets.total, 
        average: averageBudget 
      },
      completionRate,
      recommendations: recommendations.length > 0 ? recommendations : ["Your portfolio is well-optimized!"]
    };
  }, [brands]);

  // Profile completion gamification system
  const calculateProfileCompletion = useCallback(() => {
    if (!user) return { score: 0, achievements: [], nextGoals: [], level: 'Newcomer' };

    const checks = {
      // Basic Information (30 points)
      hasName: { completed: !!user.name, points: 10, label: 'Full name added' },
      hasEmail: { completed: !!user.email, points: 5, label: 'Email verified' },
      hasPhone: { completed: !!user.phone, points: 5, label: 'Phone number added' },
      hasJobTitle: { completed: !!user.jobTitle, points: 10, label: 'Job title specified' },
      
      // Company Information (25 points)
      hasCompany: { completed: !!user.company, points: 10, label: 'Company name added' },
      hasCompanyType: { completed: !!user.companyType, points: 10, label: 'Company type selected' },
      hasCompanyLogo: { completed: !!user.companyLogoUrl, points: 5, label: 'Company logo uploaded' },
      
      // Profile Enhancement (20 points)
      hasProfileImage: { completed: !!user.profileImageUrl, points: 10, label: 'Profile photo uploaded' },

      
      // Notification Preferences (15 points)
      hasNotificationSettings: { 
        completed: user.emailShowUpdates !== undefined || user.emailArtistUpdates !== undefined || user.emailMaxUpdates !== undefined, 
        points: 5, 
        label: 'Notification preferences set' 
      },
      hasOptedInUpdates: { 
        completed: user.emailShowUpdates || user.emailArtistUpdates || user.emailMaxUpdates, 
        points: 10, 
        label: 'Opted in for updates' 
      },
      
      // Advanced Features (10 points)

      isAgencyUser: { completed: user.companyType === 'agency', points: 5, label: 'Agency account setup' }
    };

    const completedChecks = Object.values(checks).filter(check => check.completed);
    const totalScore = completedChecks.reduce((sum, check) => sum + check.points, 0);
    const maxScore = Object.values(checks).reduce((sum, check) => sum + check.points, 0);
    const completionPercentage = Math.round((totalScore / maxScore) * 100);

    // Achievement system
    const achievements = [];
    if (completionPercentage >= 25) achievements.push({ icon: Star, title: 'Getting Started', color: 'text-yellow-500' });
    if (completionPercentage >= 50) achievements.push({ icon: Award, title: 'Profile Builder', color: 'text-blue-500' });
    if (completionPercentage >= 75) achievements.push({ icon: Trophy, title: 'Profile Master', color: 'text-purple-500' });
    if (completionPercentage >= 90) achievements.push({ icon: Zap, title: 'Platform Expert', color: 'text-green-500' });
    
    // Level system
    let level = 'Newcomer';
    if (completionPercentage >= 90) level = 'Platform Expert';
    else if (completionPercentage >= 75) level = 'Profile Master';
    else if (completionPercentage >= 50) level = 'Profile Builder';
    else if (completionPercentage >= 25) level = 'Getting Started';

    // Next goals (incomplete checks)
    const incompleteChecks = Object.values(checks).filter(check => !check.completed);
    const nextGoals = incompleteChecks.slice(0, 3).map(check => ({
      label: check.label,
      points: check.points
    }));

    return {
      score: completionPercentage,
      totalPoints: totalScore,
      maxPoints: maxScore,
      achievements,
      nextGoals,
      level,
      checks
    };
  }, [user]);



  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'logo') => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
        setCroppingFor(type);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    
    let crop;
    
    if (croppingFor === 'profile') {
      // Profile images use square 1:1 aspect ratio
      crop = makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1,
        naturalWidth,
        naturalHeight
      );
    } else {
      // Logo images use flexible sizing without forced aspect ratio
      // Start with 90% of the image size, user can adjust as needed
      crop = {
        unit: '%' as const,
        width: 90,
        height: 90,
        x: 5,
        y: 5,
      };
    }
    
    setCrop(centerCrop(crop, naturalWidth, naturalHeight));
    setCompletedCrop(centerCrop(crop, naturalWidth, naturalHeight));
  }, [croppingFor]);

  const getCroppedImg = useCallback((
    image: HTMLImageElement,
    crop: Crop,
    fileName: string
  ): Promise<string> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Limit canvas size to reduce file size
    const maxSize = 400; // Maximum width/height for the output image
    let canvasWidth = crop.width;
    let canvasHeight = crop.height;
    
    if (canvasWidth > maxSize || canvasHeight > maxSize) {
      const scale = Math.min(maxSize / canvasWidth, maxSize / canvasHeight);
      canvasWidth = canvasWidth * scale;
      canvasHeight = canvasHeight * scale;
    }
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Clear the canvas to transparent
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvasWidth,
        canvasHeight
      );
    }

    return new Promise((resolve) => {
      // Use PNG for logos to preserve transparency, JPEG for profiles
      const format = fileName.includes('logo') ? 'image/png' : 'image/jpeg';
      const quality = fileName.includes('logo') ? 1.0 : 0.8;
      const dataUrl = canvas.toDataURL(format, quality);
      resolve(dataUrl);
    });
  }, []);

  const handleBrandImageUpload = async (completedCrop: PixelCrop) => {
    if (!brandImageRef.current || !completedCrop.width || !completedCrop.height) return;
    
    try {
      const croppedImageUrl = await getCroppedImg(
        brandImageRef.current,
        completedCrop as Crop,
        'brand-logo'
      );

      // Update brand form data with new logo
      setBrandFormData({ ...brandFormData, logoUrl: croppedImageUrl });

      // Reset brand image states
      setBrandImageFile(null);
      setBrandImagePreview('');
      setShowBrandImageCrop(false);

      toast({
        title: "Logo uploaded",
        description: "Brand logo has been updated successfully.",
      });
    } catch (error) {
      // Error during brand logo cropping - will show in UI via error state
      toast({
        title: "Error",
        description: "Failed to process brand logo. Please try again.",
        variant: "destructive",
      });
    }
  };

  const updateProfileMutation = useMutation({
    mutationFn: async (data: z.infer<typeof profileSchema>) => {
      const updateData: any = {
        name: data.name,
        email: data.email,
        company: data.company,
        jobTitle: data.jobTitle,
        phone: data.phone,
        companyType: data.companyType,
        industry: data.industry,

        companySize: data.companySize,
        websiteUrl: data.websiteUrl,

        profileImageUrl: data.profileImageUrl,
        companyLogoUrl: data.companyLogoUrl,
        emailShowUpdates: data.emailShowUpdates,
        emailArtistUpdates: data.emailArtistUpdates,
        emailMaxUpdates: data.emailMaxUpdates,
      };
      
      // Add password fields if they're provided
      if (data.newPassword) {
        updateData.currentPassword = data.currentPassword;
        updateData.newPassword = data.newPassword;
      }
      
      // Update profile first
      const profileResponse = await apiRequest("/api/profile", { method: "PUT", body: updateData });
      
      // Check if user just changed company type to "brand" and has company info
      const previousCompanyType = user?.companyType;
      if (data.companyType === "brand" && previousCompanyType !== "brand" && data.company) {
        try {
          // Check if brand already exists with this name to avoid duplicates
          const existingBrands = await apiRequest("/api/brands");
          const brandExists = existingBrands.some((brand: any) => 
            brand.name.toLowerCase() === data.company!.toLowerCase()
          );
          
          if (!brandExists) {
            // Create automatic Brand Profile using company information
            const brandData = {
              name: data.company,
              description: `${data.company} brand profile automatically created from company information`,
              industry: data.industry || [],
              logoUrl: data.companyLogoUrl || '',
              url: data.websiteUrl || '',
              companySize: data.companySize || ''
            };
            
            const newBrand = await apiRequest("/api/brands", { method: "POST", body: brandData });
            
            // Show success message for automatic brand creation
            setTimeout(() => {
              toast({
                title: "Brand Profile Created!",
                description: `Automatically created brand profile for "${data.company}". Check Brand Management to customize it further.`,
              });
            }, 500);
          } else {
            setTimeout(() => {
              toast({
                title: "Profile Updated",
                description: `Profile updated successfully. Brand profile for "${data.company}" already exists.`,
              });
            }, 500);
          }
        } catch (error) {
          // Don't fail the profile update if brand creation fails
          // Failed to create automatic brand profile - user can create manually
          setTimeout(() => {
            toast({
              title: "Profile Updated",
              description: "Profile updated successfully. You can create a brand profile manually in Brand Management.",
            });
          }, 500);
        }
      }
      
      return profileResponse;
    },
    onSuccess: (updatedUserData) => {
      // Update the user data in cache immediately to prevent form resets
      queryClient.setQueryData(["/api/auth/me"], (oldData: any) => {
        return { ...oldData, ...updatedUserData };
      });
      form.reset({
        name: updatedUserData.name || "",
        email: updatedUserData.email || "",
        company: updatedUserData.company || "",
        jobTitle: updatedUserData.jobTitle || "",
        phone: updatedUserData.phone || "",
        companyType: (updatedUserData.companyType as "" | "brand" | "agency") || "",
        industry: updatedUserData.industry || [],

        companySize: updatedUserData.companySize || "",
        websiteUrl: updatedUserData.websiteUrl || "",
        profileImageUrl: updatedUserData.profileImageUrl || "",
        companyLogoUrl: updatedUserData.companyLogoUrl || "",
        emailShowUpdates: updatedUserData.emailShowUpdates || false,
        emailArtistUpdates: updatedUserData.emailArtistUpdates || false,
        emailMaxUpdates: updatedUserData.emailMaxUpdates || false,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      
      // Only show toast for manual profile form submissions if brand wasn't automatically created
      const wasJustChangedToBrand = updatedUserData.companyType === "brand" && user?.companyType !== "brand";
      if (!wasJustChangedToBrand) {
        toast({
          title: "Profile updated",
          description: "Your profile has been successfully updated.",
        });
      }
      
      // Invalidate with a slight delay to avoid race conditions
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      }, 100);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCropSave = useCallback(async () => {
    if (completedCrop && imageRef.current) {
      try {
        const croppedImageUrl = await getCroppedImg(
          imageRef.current,
          completedCrop,
          croppingFor === 'profile' ? 'profile-image' : 'company-logo'
        );
        
        if (croppingFor === 'profile') {
          form.setValue("profileImageUrl", croppedImageUrl);
        } else {
          form.setValue("companyLogoUrl", croppedImageUrl);
        }
        
        setShowCropModal(false);
        setImageSrc("");
        setCrop(undefined);
        setCompletedCrop(undefined);
        
        // Auto-save the form after image update
        const formData = form.getValues();
        updateProfileMutation.mutate(formData);
        
        toast({
          title: "Image updated",
          description: `Your ${croppingFor} image has been updated successfully.`,
        });
      } catch (error) {
        // Error processing image
        toast({
          title: "Error",
          description: "Failed to process the image. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [completedCrop, croppingFor, form, getCroppedImg, toast, updateProfileMutation]);

  const onSubmit = (data: z.infer<typeof profileSchema>) => {
    updateProfileMutation.mutate(data);
  };

  // Early return if no user - AFTER all hooks have been called
  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16 pt-24">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-neutral-900 mb-4">Profile</h1>
            <p className="text-lg text-neutral-600">Please sign in to view your profile.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      {/* Image Crop Modal */}
      <ImageCropModal
        open={showCropModal}
        onOpenChange={setShowCropModal}
        imageSrc={imageSrc}
        crop={crop}
        setCrop={setCrop}
        completedCrop={completedCrop}
        setCompletedCrop={setCompletedCrop}
        croppingFor={croppingFor}
        onImageLoad={onImageLoad}
        onSave={handleCropSave}
        onCancel={() => {
          setShowCropModal(false);
          setImageSrc("");
          setCrop(undefined);
          setCompletedCrop(undefined);
        }}
      />
      
      {/* Sticky Update Profile Button */}
      <div className="sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex justify-end">
            <Button 
              type="button"
              onClick={() => {
                const formData = form.getValues();
                updateProfileMutation.mutate(formData);
              }}
              disabled={updateProfileMutation.isPending}
              className="btn-max-primary shadow-lg"
            >
              {updateProfileMutation.isPending ? "Updating..." : "Update Profile"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 pt-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Profile Settings</h1>
          <p className="text-neutral-600">
            Manage your account information and preferences.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Summary & Progress */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Summary Card */}
            <ProfileHeader user={user} onImageUpload={handleImageUpload} />
          </div>

          {/* Profile Form */}
          <div className="lg:col-span-2 space-y-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Basic Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your full name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="jobTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Job Title</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your job title" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your phone number" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Company Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Company Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your company name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="companyType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select company type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="brand">Brand</SelectItem>
                                <SelectItem value="agency">Agency</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="industry"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Industry</FormLabel>
                            <FormControl>
                              <MultiSelect
                                options={INDUSTRIES.map(industry => ({ label: industry, value: industry }))}
                                selected={field.value || []}
                                onChange={field.onChange}
                                placeholder="Select industries..."
                                searchPlaceholder="Search industries..."
                                maxItems={3}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="companySize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Size</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select company size" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="1-10">1-10 employees</SelectItem>
                                <SelectItem value="11-50">11-50 employees</SelectItem>
                                <SelectItem value="51-200">51-200 employees</SelectItem>
                                <SelectItem value="201-500">201-500 employees</SelectItem>
                                <SelectItem value="501-1000">501-1000 employees</SelectItem>
                                <SelectItem value="1000+">1000+ employees</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="websiteUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Website URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://www.yourcompany.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />


                    <div className="space-y-2">
                      <FormLabel>Company Logo</FormLabel>
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-neutral-200 rounded-lg flex items-center justify-center overflow-hidden">
                          {user.companyLogoUrl && !user.companyLogoUrl.startsWith('blob:') ? (
                            <img 
                              src={user.companyLogoUrl} 
                              alt="Company Logo" 
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                // Hide image on error, fallback to icon
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Building className="h-6 w-6 text-neutral-400" />
                          )}
                        </div>
                        <label className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg cursor-pointer hover:bg-neutral-50" title="Upload and crop company logo">
                          <CropIcon className="h-4 w-4" />
                          <span className="text-sm">Upload & Crop Logo</span>
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden"
                            onChange={(e) => handleImageUpload(e, 'logo')}
                          />
                        </label>
                      </div>
                    </div>


                  </CardContent>
                </Card>

                {/* Brand Management */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Building className="h-5 w-5" />
                          Brand Management
                        </CardTitle>
                        <CardDescription>
                          Create detailed brand profiles for comprehensive campaign management and targeting.
                        </CardDescription>
                      </div>
                      {brands.length > 0 && (
                        <Popover open={showCreateMenu} onOpenChange={setShowCreateMenu}>
                          <PopoverTrigger asChild>
                            <Button className="btn-max-primary" size="sm">
                              <Plus className="h-4 w-4 mr-2" />
                              Create
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-60" align="end">
                            <div className="space-y-2">
                              <div className="text-sm font-medium text-neutral-900 mb-3">What would you like to create?</div>
                              
                              <Button
                                variant="ghost"
                                className="w-full justify-start h-auto p-3 hover:bg-blue-50"
                                onClick={() => {
                                  setBrandFormData({ userId: user?.id });
                                  setEditingBrand(null);
                                  setShowBrandModal(true);
                                  setShowCreateMenu(false);
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  <Building className="h-5 w-5 text-blue-600 mt-0.5" />
                                  <div className="text-left">
                                    <div className="font-medium text-neutral-900">Brand</div>
                                    <div className="text-xs text-neutral-600">Create a new brand profile</div>
                                  </div>
                                </div>
                              </Button>
                              
                              <Button
                                variant="ghost"
                                className="w-full justify-start h-auto p-3 hover:bg-green-50"
                                onClick={() => {
                                  if (brands.length === 1) {
                                    setSelectedBrandForAudience(brands[0]);
                                    // Removed setAudienceFormData({ brandId: brands[0].id });
                                    setEditingAudienceProfile(null);
                                    setShowAudienceModal(true);
                                  } else {
                                    setBrandSelectionType('audience');
                                    setShowBrandSelectionModal(true);
                                  }
                                  setShowCreateMenu(false);
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  <Users className="h-5 w-5 text-green-600 mt-0.5" />
                                  <div className="text-left">
                                    <div className="font-medium text-neutral-900">Personas</div>
                                    <div className="text-xs text-neutral-600">Define target demographics</div>
                                  </div>
                                </div>
                              </Button>
                              
                              <Button
                                variant="ghost"
                                className="w-full justify-start h-auto p-3 hover:bg-purple-50"
                                onClick={() => {
                                  if (brands.length === 1) {
                                    setSelectedBrandForCampaign(brands[0]);
                                    setCampaignFormData({ 
                                      brandId: brands[0].id,
                                      userId: user?.id || 0,
                                      audienceProfileId: brands[0].audienceProfiles[0]?.id || 0
                                    });
                                    setEditingCampaign(null);
                                    setShowCampaignModal(true);
                                  } else {
                                    setBrandSelectionType('campaign');
                                    setShowBrandSelectionModal(true);
                                  }
                                  setShowCreateMenu(false);
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  <TrendingUp className="h-5 w-5 text-purple-600 mt-0.5" />
                                  <div className="text-left">
                                    <div className="font-medium text-neutral-900">Campaign</div>
                                    <div className="text-xs text-neutral-600">Plan marketing initiatives</div>
                                  </div>
                                </div>
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </CardHeader>
                  
                  {/* Portfolio Insights Section */}
                  {brands.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-b">
                      <div className="px-6 py-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4" />
                            Portfolio Insights
                          </h3>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowBrandWidget(!showBrandWidget)}
                            className="h-7 text-xs text-blue-600 hover:text-blue-800"
                          >
                            {showBrandWidget ? 'Hide' : 'View'} Analytics
                          </Button>
                        </div>
                        
                        {showBrandWidget && (
                          <div className="space-y-4">
                            {/* Quick Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                <div className="flex items-center justify-center mb-1">
                                  <Target className="h-4 w-4 text-blue-600" />
                                </div>
                                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                  {brands.length}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Active Brands
                                </div>
                              </div>
                              
                              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                <div className="flex items-center justify-center mb-1">
                                  <Users className="h-4 w-4 text-green-600" />
                                </div>
                                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                  {brands.reduce((total, brand) => total + brand.audienceProfiles.length, 0)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Personas
                                </div>
                              </div>
                              
                              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                <div className="flex items-center justify-center mb-1">
                                  <TrendingUp className="h-4 w-4 text-purple-600" />
                                </div>
                                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                  {brands.reduce((total, brand) => total + brand.campaigns.length, 0)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Active Campaigns
                                </div>
                              </div>
                              
                              <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                <div className="flex items-center justify-center mb-1">
                                  <Building className="h-4 w-4 text-orange-600" />
                                </div>
                                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                  {new Set(brands.map(b => b.industry).filter(Boolean)).size}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Industries
                                </div>
                              </div>
                            </div>
                            
                            {/* Industry Distribution */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                                <PieChart className="h-4 w-4 text-gray-600" />
                                Industry Distribution
                              </h4>
                              <div className="space-y-2">
                                {(() => {
                                  const industryCount = brands.reduce((acc, brand) => {
                                    if (brand.industry) {
                                      if (Array.isArray(brand.industry)) {
                                        // Handle array of industries
                                        brand.industry.forEach(industry => {
                                          acc[industry] = (acc[industry] || 0) + 1;
                                        });
                                      } else if (typeof brand.industry === 'string') {
                                        // Handle single industry string (legacy)
                                        acc[brand.industry] = (acc[brand.industry] || 0) + 1;
                                      }
                                    }
                                    return acc;
                                  }, {} as Record<string, number>);
                                  
                                  return Object.entries(industryCount).map(([industry, count]) => (
                                    <div key={industry} className="flex items-center justify-between">
                                      <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                                        {industry}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-500"
                                            style={{ width: `${(count / brands.length) * 100}%` }}
                                          />
                                        </div>
                                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100 w-4">
                                          {count}
                                        </span>
                                      </div>
                                    </div>
                                  ));
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <CardContent className="pt-6">
                    {brandsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <MaxSpinner size="md" />
                      </div>
                    ) : brands.length === 0 ? (
                      <div className="text-center py-8">
                        <Building className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-neutral-900 mb-2">No brands yet</h3>
                        <p className="text-sm text-neutral-600 mb-4">
                          Create your first brand profile to get started with comprehensive brand management.
                        </p>
                        <Button 
                          className="btn-max-primary"
                          onClick={() => {
                            setBrandFormData({ userId: user?.id });
                            setEditingBrand(null);
                            setShowBrandModal(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create Your First Brand
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {brands.map((brand) => (
                          <div key={brand.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                {brand.logoUrl ? (
                                  <img
                                    src={brand.logoUrl}
                                    alt={`${brand.name} logo`}
                                    className="w-10 h-10 object-contain rounded"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-neutral-100 rounded flex items-center justify-center">
                                    <Building className="h-5 w-5 text-neutral-400" />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-1">
                                    <h3 className="font-semibold text-neutral-900">{brand.name}</h3>
                                    {brand.industry && Array.isArray(brand.industry) && brand.industry.length > 0 && (
                                      <div className="flex gap-1 flex-wrap">
                                        <Badge variant="outline" className="text-xs">
                                          {brand.industry[0]}
                                        </Badge>
                                        {brand.industry.length > 1 && (
                                          <Badge variant="outline" className="text-xs">
                                            +{brand.industry.length - 1} more
                                          </Badge>
                                        )}
                                      </div>
                                    )}
                                    {brand.industry && typeof brand.industry === 'string' && (
                                      <Badge variant="outline" className="text-xs">
                                        {brand.industry}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-blue-600 hover:text-blue-800 h-auto p-0"
                                      onClick={() => {
                                        setSelectedBrandForAudience(brand);
                                        setEditingAudienceProfile(null);
                                        setShowAudienceModal(true);
                                      }}
                                    >
                                      {brand.audienceProfiles.length} personas
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-green-600 hover:text-green-800 h-auto p-0"
                                      onClick={() => {
                                        setSelectedBrandForCampaign(brand);
                                        setCampaignFormData({ 
                                          brandId: brand.id,
                                          userId: user?.id || 0,
                                          audienceProfileId: brand.audienceProfiles[0]?.id || 0
                                        });
                                        setEditingCampaign(null);
                                        setShowCampaignModal(true);
                                      }}
                                    >
                                      {brand.campaigns.length} campaigns
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setBrandFormData({
                                      userId: user?.id,
                                      name: brand.name,
                                      description: brand.description || '',
                                      industry: brand.industry || [],
                                      companySize: brand.companySize || '',
                                      websiteUrl: brand.websiteUrl || '',
                                      primaryContactName: brand.primaryContactName || '',
                                      primaryContactEmail: brand.primaryContactEmail || '',
                                      primaryContactPhone: brand.primaryContactPhone || '',
                                      brandColors: brand.brandColors || [],
                                      brandGuidelinesUrl: brand.brandGuidelinesUrl || '',
                                      logoUrl: brand.logoUrl || '',
                                    });
                                    setEditingBrand(brand);
                                    setShowBrandModal(true);
                                  }}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteConfirm('brand', brand.name, brand.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Email Notifications
                    </CardTitle>
                    <CardDescription>
                      Choose which email notifications you'd like to receive
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="emailShowUpdates"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <FormLabel>Saved Show Updates</FormLabel>
                            <p className="text-sm text-neutral-500">
                              Get notified when shows you've saved have updates
                            </p>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="emailArtistUpdates"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <FormLabel>Relevant Artist Updates</FormLabel>
                            <p className="text-sm text-neutral-500">
                              Get notified about new shows from artists that match your preferences
                            </p>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="emailMaxUpdates"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <FormLabel>MAX Updates</FormLabel>
                            <p className="text-sm text-neutral-500">
                              Receive updates and news from the MAX platform
                            </p>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* Password Change */}
                <Card>
                  <CardHeader>
                    <CardTitle>Change Password</CardTitle>
                    <CardDescription>
                      Leave blank if you don't want to change your password
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input 
                                type={showCurrentPassword ? "text" : "password"} 
                                placeholder="Enter current password" 
                                {...field}
                                className="pr-10"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                              >
                                {showCurrentPassword ? (
                                  <EyeOff className="h-4 w-4 text-neutral-400" />
                                ) : (
                                  <Eye className="h-4 w-4 text-neutral-400" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="newPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>New Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input 
                                  type={showNewPassword ? "text" : "password"} 
                                  placeholder="Enter new password" 
                                  {...field}
                                  className="pr-10"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                                >
                                  {showNewPassword ? (
                                    <EyeOff className="h-4 w-4 text-neutral-400" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-neutral-400" />
                                  )}
                                </Button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Confirm New Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input 
                                  type={showConfirmPassword ? "text" : "password"} 
                                  placeholder="Confirm new password" 
                                  {...field}
                                  className="pr-10"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff className="h-4 w-4 text-neutral-400" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-neutral-400" />
                                  )}
                                </Button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>


              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* Brand Management Modal */}
      {showBrandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {editingBrand ? 'Edit Brand' : 'Create New Brand'}
                </h2>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowBrandModal(false);
                    setEditingBrand(null);
                    setBrandFormData({});
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Brand Name *
                    </label>
                    <Input
                      value={brandFormData.name || ''}
                      onChange={(e) => setBrandFormData({ ...brandFormData, name: e.target.value })}
                      placeholder="Enter brand name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Industry
                    </label>
                    <MultiSelect
                      options={INDUSTRIES.map(industry => ({ label: industry, value: industry }))}
                      selected={Array.isArray(brandFormData.industry) ? brandFormData.industry : brandFormData.industry ? [brandFormData.industry] : []}
                      onChange={(selectedIndustries) => setBrandFormData({ ...brandFormData, industry: selectedIndustries })}
                      placeholder="Select industries..."
                      searchPlaceholder="Search industries..."
                      maxItems={2}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full p-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    rows={3}
                    value={brandFormData.description || ''}
                    onChange={(e) => setBrandFormData({ ...brandFormData, description: e.target.value })}
                    placeholder="Brief description of the brand and its mission..."
                  />
                </div>

                {/* Brand Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Brand Logo
                  </label>
                  <div className="flex items-center gap-4">
                    {brandFormData.logoUrl ? (
                      <div className="relative">
                        <img 
                          src={brandFormData.logoUrl} 
                          alt="Brand logo" 
                          className="w-16 h-16 rounded border object-contain bg-white"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white hover:bg-red-600"
                          onClick={() => setBrandFormData({ ...brandFormData, logoUrl: '' })}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded border-2 border-dashed border-neutral-300 dark:border-neutral-600 flex items-center justify-center bg-neutral-50 dark:bg-neutral-800">
                        <Building className="h-6 w-6 text-neutral-400" />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('brand-logo-upload')?.click()}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {brandFormData.logoUrl ? 'Change Logo' : 'Upload Logo'}
                        </Button>
                        <input
                          id="brand-logo-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setBrandImageFile(file);
                              const reader = new FileReader();
                              reader.onload = (e) => {
                                setBrandImagePreview(e.target?.result as string);
                                setShowBrandImageCrop(true);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">
                        PNG or JPEG format. Transparent backgrounds supported.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Company Size
                    </label>
                    <select
                      className="w-full p-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                      value={brandFormData.companySize || ''}
                      onChange={(e) => setBrandFormData({ ...brandFormData, companySize: e.target.value })}
                    >
                      <option value="">Select size</option>
                      <option value="startup">Startup (1-10 employees)</option>
                      <option value="small">Small (11-50 employees)</option>
                      <option value="medium">Medium (51-200 employees)</option>
                      <option value="large">Large (201-1000 employees)</option>
                      <option value="enterprise">Enterprise (1000+ employees)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Website URL
                    </label>
                    <Input
                      value={brandFormData.websiteUrl || ''}
                      onChange={(e) => setBrandFormData({ ...brandFormData, websiteUrl: e.target.value })}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                    Primary Contact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Contact Name
                      </label>
                      <Input
                        value={brandFormData.primaryContactName || ''}
                        onChange={(e) => setBrandFormData({ ...brandFormData, primaryContactName: e.target.value })}
                        placeholder="John Smith"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={brandFormData.primaryContactEmail || ''}
                        onChange={(e) => setBrandFormData({ ...brandFormData, primaryContactEmail: e.target.value })}
                        placeholder="john@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Phone
                      </label>
                      <Input
                        value={brandFormData.primaryContactPhone || ''}
                        onChange={(e) => setBrandFormData({ ...brandFormData, primaryContactPhone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Brand Guidelines URL
                  </label>
                  <Input
                    value={brandFormData.brandGuidelinesUrl || ''}
                    onChange={(e) => setBrandFormData({ ...brandFormData, brandGuidelinesUrl: e.target.value })}
                    placeholder="Link to brand guidelines document or style guide"
                  />
                </div>
                
                {/* Personas Section - Only show when editing existing brand */}
                {editingBrand && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                        Personas ({editingBrand.audienceProfiles?.length || 0})
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBrandForAudience(editingBrand);
                          // Removed setAudienceFormData({ brandId: editingBrand.id });
                          setEditingAudienceProfile(null);
                          setShowAudienceModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Persona
                      </Button>
                    </div>
                    
                    {editingBrand.audienceProfiles && editingBrand.audienceProfiles.length > 0 ? (
                      <div className="space-y-2">
                        {editingBrand.audienceProfiles.map((profile) => (
                          <div key={profile.id} className="flex items-center justify-between p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-900">
                            <div className="flex-1">
                              <div className="font-medium text-neutral-900 dark:text-neutral-100">
                                {profile.profileName}
                              </div>
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                {profile.genders?.join(', ')} • Ages {profile.ageRanges?.join(', ')}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedBrandForAudience(editingBrand);
                                  setEditingAudienceProfile(profile);
                                  // Removed setAudienceFormData call
                                  setShowAudienceModal(true);
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteConfirm('persona', profile.profileName, profile.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-neutral-500 dark:text-neutral-400 text-sm">
                        No personas yet. Add one to define your target demographics.
                      </div>
                    )}
                  </div>
                )}
                
                {/* Campaigns Section - Only show when editing existing brand */}
                {editingBrand && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                        Campaigns ({editingBrand.campaigns?.length || 0})
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBrandForCampaign(editingBrand);
                          setCampaignFormData({ 
                            brandId: editingBrand.id,
                            userId: user?.id || 0
                          });
                          setEditingCampaign(null);
                          setShowCampaignModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Campaign
                      </Button>
                    </div>
                    
                    {editingBrand.campaigns && editingBrand.campaigns.length > 0 ? (
                      <div className="space-y-2">
                        {editingBrand.campaigns.map((campaign) => (
                          <div key={campaign.id} className="flex items-center justify-between p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-900">
                            <div className="flex-1">
                              <div className="font-medium text-neutral-900 dark:text-neutral-100">
                                {campaign.name}
                              </div>
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                {campaign.scope} • Status: {campaign.status}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedBrandForCampaign(editingBrand);
                                  setEditingCampaign(campaign);
                                  setCampaignFormData({
                                    brandId: editingBrand.id,
                                    userId: user?.id || 0,
                                    name: campaign.name || '',
                                    scope: campaign.scope || '',
                                    status: campaign.status || '',
                                    startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                    endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                    description: campaign.description || ''
                                  });
                                  setShowCampaignModal(true);
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteConfirm('campaign', campaign.name, campaign.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-neutral-500 dark:text-neutral-400 text-sm">
                        No campaigns yet. Create one to start tracking your marketing initiatives.
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowBrandModal(false);
                    setEditingBrand(null);
                    setBrandFormData({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (!brandFormData.name?.trim()) {
                      toast({
                        title: "Brand name required",
                        description: "Please enter a brand name to continue.",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    if (editingBrand) {
                      updateBrandMutation.mutate({
                        id: editingBrand.id,
                        brandData: { ...brandFormData, userId: user?.id }
                      });
                    } else {
                      createBrandMutation.mutate({ ...brandFormData, userId: user?.id } as InsertBrand);
                    }
                  }}
                  disabled={createBrandMutation.isPending || updateBrandMutation.isPending}
                  className="btn-max-primary"
                >
                  {(createBrandMutation.isPending || updateBrandMutation.isPending) && (
                    <MaxSpinner size="sm" className="mr-2" />
                  )}
                  {editingBrand ? 'Update Brand' : 'Create Brand'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brand Logo Crop Modal */}
      {showBrandImageCrop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                Crop Brand Logo
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowBrandImageCrop(false);
                  setBrandImageFile(null);
                  setBrandImagePreview('');
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="mb-4">
              <ReactCrop
                crop={brandCrop}
                onChange={(c) => setBrandCrop(c)}
                onComplete={(c) => setBrandCompletedCrop(c)}
                aspect={undefined} // Allow flexible aspect ratios for logos
              >
                <img
                  ref={brandImageRef}
                  src={brandImagePreview}
                  alt="Brand logo crop"
                  style={{ maxHeight: '400px', width: 'auto' }}
                />
              </ReactCrop>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowBrandImageCrop(false);
                  setBrandImageFile(null);
                  setBrandImagePreview('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (brandCompletedCrop && brandCompletedCrop.width && brandCompletedCrop.height) {
                    handleBrandImageUpload(brandCompletedCrop);
                  }
                }}
                disabled={!brandCompletedCrop?.width || !brandCompletedCrop?.height}
                className="btn-max-primary"
              >
                <CropIcon className="h-4 w-4 mr-2" />
                Apply Logo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Persona Management Modal */}
      {showAudienceModal && selectedBrandForAudience && (
        <QuickPersonaCreator
          initialData={editingAudienceProfile && selectedBrandForAudience ? {
            ...editingAudienceProfile,
            brandId: selectedBrandForAudience.id
          } : undefined}
          onClose={() => {
            setShowAudienceModal(false);
            setSelectedBrandForAudience(null);
            setEditingAudienceProfile(null);
          }}
          onPersonaCreated={(persona) => {
            // Refresh brands data to show updated profiles
            queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
            setShowAudienceModal(false);
            setSelectedBrandForAudience(null);
            setEditingAudienceProfile(null);
            toast({
              title: editingAudienceProfile ? "Profile Updated" : "Profile Created",
              description: `${persona.profileName} has been ${editingAudienceProfile ? 'updated' : 'created'} successfully.`,
            });
          }}
        />
      )}

      {/* Campaign Management Modal */}
      {showCampaignModal && selectedBrandForCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                    {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
                  </h2>
                  <p className="text-sm text-neutral-600">for {selectedBrandForCampaign.name}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCampaignModal(false);
                    setSelectedBrandForCampaign(null);
                    setEditingCampaign(null);
                    setCampaignFormData({});
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Show existing campaigns first */}
              {selectedBrandForCampaign.campaigns.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Existing Campaigns</h3>
                  <div className="space-y-2">
                    {selectedBrandForCampaign.campaigns.map((campaign) => (
                      <div key={campaign.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <span className="font-medium">{campaign.name}</span>
                          <span className="text-sm text-neutral-600 ml-2">
                            {campaign.status} • Budget: {campaign.budgetRange || 'Not set'}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingCampaign(campaign);
                              setCampaignFormData({
                                brandId: selectedBrandForCampaign.id,
                                audienceProfileId: campaign.audienceProfileId || 0,
                                name: campaign.name,
                                description: campaign.description || '',
                                budgetRange: campaign.budgetRange || '',
                                status: campaign.status || 'draft',
                                startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
                                endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
                                campaignObjective: campaign.campaignObjective || '',
                                scope: campaign.scope || '',
                              });
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Delete "${campaign.name}" campaign?`)) {
                                deleteCampaignMutation.mutate(campaign.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-3">Add New Campaign</h3>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Campaign Name *
                  </label>
                  <Input
                    value={campaignFormData.name || ''}
                    onChange={(e) => setCampaignFormData({ ...campaignFormData, name: e.target.value })}
                    placeholder="e.g., Summer Festival Campaign 2024"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full p-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    rows={3}
                    value={campaignFormData.description || ''}
                    onChange={(e) => setCampaignFormData({ ...campaignFormData, description: e.target.value })}
                    placeholder="Campaign objectives and key messaging..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Campaign Budget
                    </label>
                    <Input
                      type="text"
                      value={campaignFormData.budgetRange || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Remove all non-numeric characters except decimal points
                        const numericValue = value.replace(/[^\d.]/g, '');
                        
                        // Don't allow multiple decimal points
                        const parts = numericValue.split('.');
                        let cleanValue = parts[0];
                        if (parts.length > 1) {
                          cleanValue += '.' + parts.slice(1).join('').slice(0, 2); // Limit to 2 decimal places
                        }
                        
                        // Format with commas and dollar sign
                        if (cleanValue && !isNaN(parseFloat(cleanValue))) {
                          const number = parseFloat(cleanValue);
                          let formatted;
                          if (cleanValue.includes('.')) {
                            // Preserve decimal input while typing
                            const [wholePart, decimalPart = ''] = cleanValue.split('.');
                            const formattedWhole = parseInt(wholePart || '0').toLocaleString();
                            formatted = decimalPart !== '' ? `$${formattedWhole}.${decimalPart}` : `$${formattedWhole}.`;
                          } else {
                            formatted = `$${number.toLocaleString()}`;
                          }
                          setCampaignFormData({ ...campaignFormData, budgetRange: formatted });
                        } else if (cleanValue === '') {
                          setCampaignFormData({ ...campaignFormData, budgetRange: '' });
                        }
                      }}
                      placeholder="Enter budget amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Persona
                    </label>
                    <select
                      className="w-full p-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                      value={campaignFormData.audienceProfileId || ''}
                      onChange={(e) => setCampaignFormData({ ...campaignFormData, audienceProfileId: parseInt(e.target.value) || 0 })}
                    >
                      <option value="">Select audience profile</option>
                      {selectedBrandForCampaign?.audienceProfiles.map(profile => (
                        <option key={profile.id} value={profile.id}>{profile.profileName}</option>
                      ))}
                    </select>
                    {selectedBrandForCampaign?.audienceProfiles.length === 0 && (
                      <p className="text-sm text-neutral-500 mt-1">Create an audience profile first to target campaigns</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Campaign Objective
                    </label>
                    <select
                      className="w-full p-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                      value={campaignFormData.campaignObjective || ''}
                      onChange={(e) => setCampaignFormData({ ...campaignFormData, campaignObjective: e.target.value })}
                    >
                      <option value="">Select objective</option>
                      <option value="brand_awareness">Brand Awareness</option>
                      <option value="conversions">Conversions</option>
                      <option value="engagement">Engagement</option>
                      <option value="lead_generation">Lead Generation</option>
                      <option value="sales">Sales</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Scope
                    </label>
                    <select
                      className="w-full p-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                      value={campaignFormData.scope || ''}
                      onChange={(e) => setCampaignFormData({ ...campaignFormData, scope: e.target.value })}
                    >
                      <option value="">Select scope</option>
                      <option value="local">Local</option>
                      <option value="regional">Regional</option>
                      <option value="national">National</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Start Date
                    </label>
                    <Input
                      type="date"
                      value={typeof campaignFormData.startDate === 'string' ? campaignFormData.startDate : ''}
                      onChange={(e) => setCampaignFormData({ ...campaignFormData, startDate: e.target.value as any })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      End Date
                    </label>
                    <Input
                      type="date"
                      value={typeof campaignFormData.endDate === 'string' ? campaignFormData.endDate : ''}
                      onChange={(e) => setCampaignFormData({ ...campaignFormData, endDate: e.target.value as any })}
                    />
                  </div>
                </div>


              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCampaignModal(false);
                    setSelectedBrandForCampaign(null);
                    setEditingCampaign(null);
                    setCampaignFormData({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (!campaignFormData.name?.trim()) {
                      toast({
                        title: "Campaign name required",
                        description: "Please enter a campaign name to continue.",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    if (!campaignFormData.audienceProfileId) {
                      toast({
                        title: "Audience profile required",
                        description: "Please select an audience profile to continue.",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    if (editingCampaign) {
                      const campaignDataToUpdate = {
                        ...campaignFormData,
                        brandId: selectedBrandForCampaign?.id,
                        audienceProfileId: campaignFormData.audienceProfileId,
                        startDate: campaignFormData.startDate ? new Date(campaignFormData.startDate) : undefined,
                        endDate: campaignFormData.endDate ? new Date(campaignFormData.endDate) : undefined,
                      };
                      updateCampaignMutation.mutate({
                        id: editingCampaign.id,
                        campaignData: campaignDataToUpdate
                      });
                    } else {
                      const campaignDataToSubmit = {
                        ...campaignFormData,
                        brandId: selectedBrandForCampaign?.id,
                        audienceProfileId: campaignFormData.audienceProfileId,
                        startDate: campaignFormData.startDate ? new Date(campaignFormData.startDate) : undefined,
                        endDate: campaignFormData.endDate ? new Date(campaignFormData.endDate) : undefined,
                      };
                      // Submitting campaign data
                      createCampaignMutation.mutate(campaignDataToSubmit as InsertCampaign);
                    }
                  }}
                  disabled={createCampaignMutation.isPending || updateCampaignMutation.isPending}
                  className="btn-max-primary"
                >
                  {(createCampaignMutation.isPending || updateCampaignMutation.isPending) && (
                    <MaxSpinner size="sm" className="mr-2" />
                  )}
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brand Selection Modal */}
      <Dialog open={showBrandSelectionModal} onOpenChange={setShowBrandSelectionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Select Brand for {brandSelectionType === 'audience' ? 'Persona' : 'Campaign'}
            </DialogTitle>
            <DialogDescription>
              Choose which brand you'd like to create {brandSelectionType === 'audience' ? 'an audience profile' : 'a campaign'} for.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {brands.map((brand) => (
              <Button
                key={brand.id}
                variant="outline"
                className="w-full justify-start h-auto p-4 hover:bg-neutral-50"
                onClick={() => {
                  if (brandSelectionType === 'audience') {
                    setSelectedBrandForAudience(brand);
                    // Removed setAudienceFormData({ brandId: brand.id });
                    setEditingAudienceProfile(null);
                    setShowAudienceModal(true);
                  } else if (brandSelectionType === 'campaign') {
                    setSelectedBrandForCampaign(brand);
                    setCampaignFormData({ 
                      brandId: brand.id,
                      userId: user?.id || 0,
                      audienceProfileId: brand.audienceProfiles[0]?.id || 0
                    });
                    setEditingCampaign(null);
                    setShowCampaignModal(true);
                  }
                  setShowBrandSelectionModal(false);
                  setBrandSelectionType(null);
                }}
              >
                <div className="flex items-center gap-3 w-full">
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={`${brand.name} logo`}
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-neutral-100 rounded flex items-center justify-center">
                      <Building className="h-5 w-5 text-neutral-400" />
                    </div>
                  )}
                  <div className="flex-1 text-left">
                    <div className="font-medium text-neutral-900">{brand.name}</div>
                    <div className="text-sm text-neutral-600">
                      {brandSelectionType === 'audience' 
                        ? `${brand.audienceProfiles.length} personas`
                        : `${brand.campaigns.length} campaigns`
                      }
                    </div>
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        deleteConfirmData={deleteConfirmData}
        onConfirm={executeDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteConfirmData(null);
        }}
        isDeleting={deleteBrandMutation.isPending || deleteAudienceProfileMutation.isPending || deleteCampaignMutation.isPending}
      />
    </div>
  );
}